import {DelaysMetrics} from '../models/removals/delays-metrics.model';
import {RemovalsMetrics} from '../models/removals/removals-metrics.model';
import {PartMetrics} from '../models/removals/part-metrics.model';
import {YearOverYearComparison} from '../models/year-over-year-comparison.model';
import {RemovalPivot} from '../models/removal-pivot.model';
import {BaseUnitHistory} from '../models';
import {Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Burly} from 'kb-burly';
import {map} from 'rxjs/operators';
import {SortDirection} from '@angular/material/sort';
import {PaginationResponse} from '../../../responses/pagination.response';
import {BaseUnitHistoryResponse} from '../responses';
import {TorchUtils} from '../../../../app/utils/torch.utils';
import {Cacheable} from 'ts-cacheable';
import {RemovalHeatmap} from '../models/removal-heatmap.model';
import {ManyResponse} from '../../../responses';
import {environment} from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TorchRemovalsService {

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient) {
  }

  public getFleetModelCodes(): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/smart-predictions')
      .addSegment('/fleet')
      .get;

    return this.httpClient.get<[{ fleetModelCode: string }]>(endpoint).pipe(
      map(response => response.map(v => v.fleetModelCode))
    );
  }

  @Cacheable()
  public getDistinctValues(column: string) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/values')
      .addSegment('/' + column)
      .get;

    return this.httpClient.get<any[]>(endpoint);
  }

  public getRemovals(pageSize: number = 15,
                     currentPage: number = 0,
                     sortDirection?: SortDirection,
                     sortBy?: string,
                     filter?: string | string[],
                     orFilter?: string | string[]): Observable<BaseUnitHistoryResponse> {

    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addQuery('page', (currentPage + 1))
      .addQuery('limit', pageSize);

    if (sortDirection && sortBy) {
      endpoint.addQuery('sort', `${sortBy},${sortDirection.toUpperCase()}`);
    }

    finalURL = endpoint.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach(filterValue => {
            finalURL = `${finalURL}&filter=${encodeURIComponent(filterValue)}`;
          });
        }
      } else {
        finalURL = `${finalURL}&or=${encodeURIComponent(filter)}`;
      }
    }

    if (orFilter) {
      if (orFilter instanceof Array) {
        if (orFilter.length > 0) {
          orFilter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}&filter=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&or=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}&or=${encodeURIComponent(orFilter)}`;
      }
    }

    return this.httpClient.get<PaginationResponse>(finalURL);
  }

  @Cacheable()
  public sendQueryRemovalsRequest(pageSize: number = 15,
                                  currentPage: number = 0,
                                  sortDirection?: SortDirection,
                                  sortBy?: string,
                                  search?: any) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/many')
      .get;

    const request = {
      sort: null,
      s: null,
      page: (currentPage + 1),
      limit: pageSize
    };


    if (sortDirection && sortBy) {
      request.sort = `${sortBy},${sortDirection.toUpperCase()}`;
    }


    if (!!search) {
      request.s = JSON.stringify(search);
    }

    return this.httpClient.post<ManyResponse<BaseUnitHistory>>(endpoint, request);
  }

  @Cacheable()
  public getRemovalTwoColumnRate(column1: string,
                                 column2: string,
                                 filter?: string | string[]): Observable<number> {

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/rate/' + column1 + '/' + column2);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<number>(finalURL);
  }

  @Cacheable()
  public getRemovalYoYTwoColumnRate(column1: string,
                                    column2: string,
                                    firstDate: string,
                                    midDate: string,
                                    rmvlOrInstall: string,
                                    filter?: string | string[]): Observable<YearOverYearComparison> {

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/yoy/rate/' + column1 + '/' + column2 + '/' + firstDate + '/' + midDate + '/' + rmvlOrInstall);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<YearOverYearComparison>(finalURL);
  }

  @Cacheable()
  public getRemovalColumnSum(column: string,
                             filter?: string | string[]): Observable<number> {

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/sum/' + column);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<number>(finalURL);
  }

  @Cacheable()
  public getRemovalYoYColumnSum(column: string,
                                firstDate: string,
                                midDate: string,
                                rmvlOrInstall: string,
                                filter?: string | string[]): Observable<YearOverYearComparison> {

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/yoy/sum/' + column + '/' + firstDate + '/' + midDate + '/' + rmvlOrInstall);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<YearOverYearComparison>(finalURL);
  }

  @Cacheable()
  public getRemovalColumnPercentile(percentile: number,
                                    column: string,
                                    filter?: string | string[]): Observable<number> {

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/percentile/' + percentile + '/' + column);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<number>(finalURL);
  }

  @Cacheable()
  public getRemovalYoYColumnPercentile(percentile: number,
                                       column: string,
                                       firstDate: string,
                                       midDate: string,
                                       rmvlOrInstall: string,
                                       filter?: string | string[]): Observable<YearOverYearComparison> {

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/yoy/percentile/' + percentile + '/' + column + '/' + firstDate + '/' + midDate + '/' + rmvlOrInstall);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<YearOverYearComparison>(finalURL);
  }

  @Cacheable()
  public getRemovalPivots(column: string,
                          filter?: string | string[],
                          fieldSort?: string): Observable<RemovalPivot[]> {

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/pivot/' + column);

    if (fieldSort === 'true') {
      endpointURL.addSegment('/true');
    } else {
      endpointURL.addSegment('/false');
    }

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<RemovalPivot[]>(finalURL);
  }

  @Cacheable()
  public getRemovalHeatmapSum(column1: string,
                              column2: string,
                              aggregate: string,
                              filter?: string | string[]): Observable<RemovalHeatmap[]> {

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/heatmap/sum/' + column1 + '/' + column2 + '/' + aggregate);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<RemovalHeatmap[]>(finalURL);
  }

  @Cacheable()
  public getRemovalHeatmapRate(column1: string,
                               column2: string,
                               aggregate: string,
                               denominator: string,
                               filter?: string | string[]): Observable<RemovalHeatmap[]> {

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/heatmap/rate/' + column1 + '/' + column2 + '/' + aggregate + '/' + denominator);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<RemovalHeatmap[]>(finalURL);
  }

  @Cacheable({
    maxCacheCount: 150
  })
  public getPartMetrics(filter?: string | string[]): Observable<PartMetrics[]> {

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/part-metrics');

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<PartMetrics[]>(finalURL);
  }

  @Cacheable({
    maxCacheCount: 150
  })
  public getRemovalsMetrics(filter?: string | string[]): Observable<RemovalsMetrics[]> {

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/removals-metrics');

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<RemovalsMetrics[]>(finalURL);
  }

  @Cacheable({
    maxCacheCount: 150
  })
  public getDelaysMetrics(filter?: string | string[]): Observable<DelaysMetrics[]> {

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/delays-metrics');

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<DelaysMetrics[]>(finalURL);
  }

  public exportRemovalsToExcel(removals: BaseUnitHistory[]) {
    return new Observable(subscriber => {
      const endpoint = Burly(this.config.rootUrl)
        .addSegment('/removals')
        .addSegment('/export')
        .get;

      const httpOptions: { headers; observe; responseType } = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        }),
        observe: 'response',
        responseType: 'blob'
      };

      const body = {
        removals
      };

      this.httpClient.post(endpoint, body, httpOptions).subscribe((response) => {
        subscriber.next((response as unknown as HttpResponse<any>).body);
        subscriber.complete();
      });
    });
  }

  public exportVendorRemovalsToExcel(removals: BaseUnitHistory[]) {
    return new Observable(subscriber => {
      const endpoint = Burly(this.config.rootUrl)
        .addSegment('/removals')
        .addSegment('/vendor')
        .get;

      const httpOptions: { headers; observe; responseType } = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        }),
        observe: 'response',
        responseType: 'blob'
      };

      const body = {
        removals
      };

      this.httpClient.post(endpoint, body, httpOptions).subscribe((response) => {
        subscriber.next((response as unknown as HttpResponse<any>).body);
        subscriber.complete();
      });
    });
  }

  public getRemovalCodes(filter?: string | string[]): Observable<string[]> {
    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/removalCodes');

    finalURL = endpoint.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<[{ removalReasonCode: string }]>(finalURL).pipe(
      map(response => response.map(v => v.removalReasonCode))
    );
  }

  public getFleets(filter?: string | string[]): Observable<string[]> {
    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/fleets');

    finalURL = endpoint.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<[{ fleetCode: string }]>(finalURL).pipe(
      map(response => response.map(v => v.fleetCode))
    );
  }

  public getMaxTsi(filter?: string | string[]): Observable<number> {
    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/maxTsi');

    finalURL = endpoint.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<{ tsi: number }>(finalURL).pipe(
      map(response => response.tsi)
    );
  }

  public getMinRemovalDate(filter?: string | string[]): Observable<string> {
    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/minDate');

    finalURL = endpoint.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<{ minRemovalDate: Date }>(finalURL).pipe(
      map(response => TorchUtils.getDatePart(response.minRemovalDate).toISODateString())
    );
  }

  public getMaxRemovalDate(filter?: string | string[]): Observable<string> {
    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/maxDate');

    finalURL = endpoint.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<{ maxRemovalDate: Date }>(finalURL).pipe(
      map(response => TorchUtils.getDatePart(response.maxRemovalDate).toISODateString())
    );
  }

  public fullExportToExcel(search: any, limit: number) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/exportFull')
      .get;

    const httpOptions: { headers; observe; responseType } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      observe: 'response',
      responseType: 'blob'
    };

    const request = {
      limit: (limit > environment.maxRemovalQueryExport ? environment.maxRemovalQueryExport : limit),
      s: null,
    };


    if (!!search) {
      request.s = JSON.stringify(search);
    }

    return this.httpClient.post(endpoint, request, httpOptions).pipe(
      map(response => (response as unknown as HttpResponse<any>).body)
    );
  }

  // @Cacheable()
  // public isHistoryRecordInstalled(partNumber: string, partSerial: string) {
  //   const endpoint = Burly(this.config.rootUrl)
  //     .addSegment('/removals')
  //     .addSegment('/latest')
  //     .addSegment('/' + partNumber)
  //     .addSegment('/' + partSerial)
  //     .addSegment('/installed')
  //     .get;
  //
  //   return this.httpClient.get<boolean>(endpoint);
  // }

  public isHistoryRecordInstalled(partNumber: string, partSerial: string): Observable<boolean> {
    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/latest')
      .addSegment('/' + partNumber)
      .addSegment('/' + partSerial)
      .addSegment('/installed');

    finalURL = endpoint.get;

    return this.httpClient.get<boolean>(finalURL).pipe(
      map(response => response)
    );
  }

  public getLatestRecord(partNumber: string, partSerial: string): Observable<BaseUnitHistory> {
    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/removals')
      .addSegment('/latest')
      .addSegment('/' + partNumber)
      .addSegment('/' + partSerial);

    finalURL = endpoint.get;

    return this.httpClient.get<BaseUnitHistory[]>(finalURL).pipe(
      map(response => response[0])
    );
  }
}
